import { use, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { cva } from 'class-variance-authority'
import { Input } from '@/components/ui'
import { FixedTableCell, RowHeader, TableCell, TableCellContent } from '@/components/ui/table'
import { TableContext, TableRowContext, TableRowType } from './table-context'
import { cn } from '@/lib/utils'
import { useGetPeriodType } from '@/lib/hooks/useGetPeriodType'

export interface TableCellProps extends React.ComponentProps<typeof TableCell> {
  cell_period: string
}

const PeriodFixedTableCell = ({ ref, className, ...props }: React.ComponentPropsWithRef<typeof FixedTableCell>) => {
  const context = use(TableRowContext)

  if (!context) {
    if (import.meta.env.DEV) throw new Error(`TableRowContext not found`)

    return <FixedTableCell ref={ref} {...props} className={className} />
  }

  const { type } = context

  return (
    <FixedTableCell
      ref={ref}
      {...props}
      className={cn(className, {
        'bg-blue-50 hover:bg-blue-50': type === `totals` || type === `total-input`,
        'bg-blue-100 hover:bg-blue-100': type === `totals-dark`
      })}
    />
  )
}

PeriodFixedTableCell.displayName = `PeriodFixedTableCell`

type PeriodRowHeaderProps = React.ComponentPropsWithRef<typeof RowHeader>

const PeriodRowHeader = ({ ref, type: overrideType, ...props }: PeriodRowHeaderProps) => {
  const context = use(TableRowContext)

  if (!context) {
    if (import.meta.env.DEV) {
      throw new Error(`TableRowContext not found`)
    }
    return <RowHeader ref={ref} {...props} />
  }

  const { period } = use(TableContext)
  const { type } = context

  return (
    <RowHeader
      ref={ref}
      {...props}
      type={`output`}
      className={cn(props.className, {
        'text-secondary': type === `output` || type === `input`,
        'font-medium': type === `totals` || type === `totals-dark`,
        'min-w-60': period === `forecast`
      })}
    />
  )
}

PeriodRowHeader.displayName = `PeriodRowHeader`

type PeriodTableCellContentProps = React.ComponentPropsWithRef<typeof TableCellContent>

const getCellType = (type: TableRowType) => {
  if (type === `total-input`) return `input`

  if (type === `totals-dark`) return `output`

  if (type === `totals-white`) return `totals`

  return type
}

const PeriodCellContent = ({ ref, ...props }: PeriodTableCellContentProps) => {
  const context = use(TableRowContext)

  if (!context) {
    if (import.meta.env.DEV) throw new Error(`TableRowContext not found`)

    return <TableCellContent ref={ref} {...props} />
  }

  const { type } = context
  const typeToUse = getCellType(props.type || type)

  return <TableCellContent ref={ref} {...props} type={typeToUse} />
}

PeriodCellContent.displayName = `PeriodCellContent`

const PeriodTableCell = ({ ref, cell_period, ...props }: TableCellProps) => {
  const context = use(TableContext)

  if (!context) throw new Error(`TableContext not found`)

  const { lastHistoricalPeriod, queryPeriod } = context

  return (
    <TableCell
      ref={ref}
      {...props}
      className={cn(`text-sm`, props.className, {
        'border-r-[4px] border-gray-200': cell_period === lastHistoricalPeriod && queryPeriod === `all`
      })}
    />
  )
}

const PeriodTableHeaderContent = ({
  ref,
  className,
  children,
  fiscal_year,
  cell_period,
  isActualized,
  ...props
}: React.ComponentPropsWithRef<'div'> & {
  fiscal_year?: number | null
  cell_period: string
  isActualized?: boolean
}) => {
  const context = use(TableContext)

  if (!context) throw new Error(`TableContext not found`)

  const { queryPeriod } = context
  const { id: financialModelId } = useParams<{ id: string }>()
  const getPeriodType = useGetPeriodType(financialModelId!)
  const period = useMemo(() => getPeriodType(cell_period), [cell_period, getPeriodType])
  const { t: commonT } = useTranslation(`common`)

  return (
    <div ref={ref} {...props} className={cn(`flex flex-col items-center`, className)}>
      {queryPeriod === `all` && period && period.length > 0 ? (
        <span className='text-xs text-blue-400'>
          {period.charAt(0).toUpperCase() + period.slice(1)}
          {isActualized ? ` Actualized` : ``}
        </span>
      ) : (
        fiscal_year && (
          <span className='text-xs text-blue-400'>
            {commonT(`table.month_fy`, {
              fy: fiscal_year
            })}{' '}
            {isActualized ? `Actualized` : ``}
          </span>
        )
      )}
      <span className='text-sm'>{children}</span>
    </div>
  )
}

PeriodTableHeaderContent.displayName = `PeriodTableHeaderContent`

type PeriodTableInputProps = React.ComponentPropsWithRef<typeof Input>

const periodTableInputVariants = cva(`table-input h-10 text-right text-sm w-full flex-grow bg-transparent`, {
  variants: {
    hover: {
      input: ``,
      'total-input': `bg-blue-50 bg-transparent focus:bg-blue-100 focus:outline-none! focus:border-none focus:ring-0`
    } as Record<TableRowType, string>
  },
  defaultVariants: {
    hover: `input`
  }
})

export const PeriodTableInput = ({ ref, className, ...props }: PeriodTableInputProps) => {
  const { type } = use(TableRowContext)

  return <Input ref={ref} {...props} className={cn(periodTableInputVariants({ hover: type }), className)} />
}

export { PeriodTableCell, PeriodTableHeaderContent, PeriodFixedTableCell, PeriodRowHeader, PeriodCellContent }
